import { useState, useEffect } from "react";
import { submitCrmLead } from "@/lib/leads";
import { THANK_YOU } from "@/lib/thankYou";
import { useNavigate } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, Home, BedDouble, Bath, Car, ArrowUp, Maximize, Waves, Flame, Sun, MapPin, Wind, Phone } from "lucide-react";
import WhatsAppFloatingButton, { WHATSAPP_NUMBER } from "@/components/WhatsAppFloatingButton";
import ScheduleVisitDialog from "@/components/ScheduleVisitDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";


import piscinaVista from "@/assets/cobertura/piscina-vista-cobertura.jpg";
import piscinaCobertura from "@/assets/cobertura/piscina-cobertura.jpg";
import salaECozinha from "@/assets/cobertura/sala-e-cozinha.jpg";
import salaEscada from "@/assets/cobertura/sala-escada-caracol.jpg";
import quartoBeliche from "@/assets/cobertura/quarto-beliche.jpg";
import banheiro from "@/assets/cobertura/banheiro.jpg";
import banheiroDetalhes from "@/assets/cobertura/banheiro-detalhes.jpg";
import fachada from "@/assets/cobertura/fachada.jpg";

const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Olá! Tenho interesse na Cobertura Duplex no Parque das Rosas - R$ 1.600.000. Gostaria de agendar uma visita!")}`;

const images = [
  { src: piscinaVista, alt: "Piscina privativa com vista panorâmica da cobertura" },
  { src: piscinaCobertura, alt: "Área da piscina na cobertura" },
  { src: salaECozinha, alt: "Sala e cozinha integradas" },
  { src: salaEscada, alt: "Sala ampla com dois pavimentos" },
  { src: quartoBeliche, alt: "Quarto com beliche" },
  { src: banheiro, alt: "Banheiro da suíte" },
  { src: banheiroDetalhes, alt: "Detalhes do banheiro" },
  { src: fachada, alt: "Fachada do condomínio Parque das Rosas" },
];

const highlights = [
  { icon: Waves, title: "Piscina Privativa", desc: "Área externa exclusiva no terraço com piscina para você e sua família" },
  { icon: Flame, title: "Churrasqueira", desc: "Espaço gourmet com churrasqueira e pia de apoio na cobertura" },
  { icon: Sun, title: "Vista Panorâmica", desc: "Andar alto com vista deslumbrante da Barra da Tijuca" },
  { icon: MapPin, title: "100m do Shopping", desc: "Localização privilegiada com acesso rápido a comércio e serviços" },
  { icon: Wind, title: "Ar-condicionado", desc: "Climatização em todos os ambientes para conforto o ano todo" },
];

import { usePageMeta } from "@/hooks/usePageMeta";

const CoberturaParqueDasRosas = () => {
  usePageMeta({
    description: "Cobertura Duplex Barra da Tijuca • 3 quartos (2 suítes) · 140m² · Andar alto · Piscina privativa e churrasqueira · Parque das Rosas",
    keywords: "Cobertura Duplex Parque das Rosas, Cobertura 3 quartos Barra da Tijuca, Cobertura piscina privativa Barra, Cobertura Barra da Tijuca, Topclass Imóveis",
  });
  const navigate = useNavigate();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [leadForm, setLeadForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [contactForm, setContactForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [contactSubmitted, setContactSubmitted] = useState(false);

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "RealEstateListing",
      name: "Cobertura Duplex à Venda - Parque das Rosas, Barra da Tijuca",
      description: "Cobertura duplex de 140m² com 3 quartos, 2 suítes, piscina privativa e churrasqueira no Parque das Rosas, Barra da Tijuca.",
      url: window.location.href,
      price: "1600000",
      priceCurrency: "BRL",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Avenida Marechal Henrique Lott, 70",
        addressLocality: "Rio de Janeiro",
        addressRegion: "RJ",
        addressCountry: "BR",
      },
      numberOfRooms: 3,
      numberOfBathroomsTotal: 2,
      floorSize: { "@type": "QuantitativeValue", value: 140, unitCode: "MTK" },
    });
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);

  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const prev = () => setLightboxIndex((i) => (i !== null ? (i - 1 + images.length) % images.length : null));
  const next = () => setLightboxIndex((i) => (i !== null ? (i + 1) % images.length : null));

  const handleLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadForm.name.trim() || !leadForm.phone.trim()) return;
    const { error } = await submitCrmLead({
      name: leadForm.name,
      phone: leadForm.phone,
      email: leadForm.email,
      propertyName: "Busca outras coberturas — Parque das Rosas",
      neighborhood: "Barra da Tijuca",
      notes: [leadForm.message.trim() && `Mensagem: ${leadForm.message.trim()}`].filter(Boolean) as string[],
    });
    if (error) return;
    setLeadSubmitted(true);
    navigate(THANK_YOU.imoveis);
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactForm.name.trim() || !contactForm.phone.trim()) return;
    const { error } = await submitCrmLead({
      name: contactForm.name,
      phone: contactForm.phone,
      email: contactForm.email,
      propertyName: "Agende sua visita — Cobertura Duplex Parque das Rosas",
      neighborhood: "Barra da Tijuca",
      temperature: "quente",
      notes: [contactForm.message.trim() && `Mensagem: ${contactForm.message.trim()}`].filter(Boolean) as string[],
    });
    if (error) return;
    setContactSubmitted(true);
    navigate(THANK_YOU.imoveis);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-md border-b border-border/30 shadow-sm">
        <div className="container mx-auto px-4 flex items-center justify-between h-16">
          <a href="/" className="font-display text-lg font-bold text-primary">TOPCLASS IMÓVEIS</a>
          <nav className="hidden md:flex items-center gap-6 font-body text-sm text-muted-foreground">
            <a href="#detalhes" className="hover:text-primary transition-colors">Detalhes</a>
            <a href="#fotos" className="hover:text-primary transition-colors">Fotos</a>
            <a href="#descricao" className="hover:text-primary transition-colors">Descrição</a>
            <a href="#localizacao" className="hover:text-primary transition-colors">Localização</a>
          </nav>
          <div className="flex items-center gap-3">
            <a href={`tel:+${WHATSAPP_NUMBER}`} className="hidden sm:flex items-center gap-1 font-body text-sm text-muted-foreground hover:text-primary transition-colors">
              <Phone className="w-4 h-4" />
              +55 21 97354-8387
            </a>
            <ScheduleVisitDialog
              propertyName="Cobertura Duplex no Parque das Rosas"
              neighborhood="Barra da Tijuca"
              variant="gold"
              size="sm"
              label="Agendar Visita"
            />
          </div>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="relative min-h-[90vh] flex items-end">
          <img src={piscinaVista} alt="Cobertura duplex com piscina privativa no Parque das Rosas, Barra da Tijuca" className="absolute inset-0 w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
          <div className="relative z-10 container mx-auto px-4 pb-16 pt-32">
            <span className="inline-block bg-accent text-accent-foreground text-sm font-semibold font-body px-4 py-1.5 rounded-full mb-6 uppercase tracking-wider">
              Oportunidade Exclusiva
            </span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight max-w-3xl mb-4">
              Cobertura Duplex com Piscina Privativa no Parque das Rosas
            </h1>
            <p className="text-white/80 font-body text-lg md:text-xl max-w-2xl mb-2">
              3 quartos · 2 suítes · 140m² · Andar alto · Barra da Tijuca, RJ
            </p>
            <p className="font-display text-3xl md:text-4xl font-bold text-accent mb-8">
              R$ 1.600.000
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <Button variant="gold" size="lg" className="text-lg px-8 py-4">
                  <Phone className="w-5 h-5" />
                  Tire suas dúvidas pelo Whatsapp
                </Button>
              </a>
              <ScheduleVisitDialog
                propertyName="Cobertura Duplex no Parque das Rosas"
                neighborhood="Barra da Tijuca"
                variant="outline"
                size="lg"
                className="text-lg px-8 py-4 border-white/30 text-white hover:bg-white/10"
                label="Quero agendar uma visita"
              />
            </div>
          </div>
        </section>

        {/* Price Card & Features */}
        <section className="py-12 bg-card" id="detalhes">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <h2 className="font-display text-3xl font-bold text-foreground mb-8">Cômodos e Espaços</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                  {[
                    { icon: BedDouble, label: "3 Quartos", sub: "Incluindo 2 suítes" },
                    { icon: Bath, label: "2 Banheiros", sub: "Suítes e social" },
                    { icon: Car, label: "1 Vaga", sub: "Garagem coberta" },
                    { icon: Maximize, label: "140m²", sub: "Área total" },
                    { icon: ArrowUp, label: "Andar Alto", sub: "Acima do 4º andar" },
                    { icon: Home, label: "Cobertura Duplex", sub: "2 pavimentos" },
                  ].map((item) => (
                    <div key={item.label} className="flex items-start gap-3 p-4 rounded-lg bg-muted">
                      <item.icon className="w-6 h-6 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-body font-semibold text-foreground">{item.label}</p>
                        <p className="font-body text-sm text-muted-foreground">{item.sub}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-secondary text-secondary-foreground rounded-xl p-8 flex flex-col justify-between shadow-lg">
                <div>
                  <p className="font-body text-secondary-foreground/70 text-sm uppercase tracking-wider mb-1">Valor de Venda</p>
                  <p className="font-display text-4xl font-bold text-accent mb-6">R$ 1.600.000</p>
                  <div className="space-y-3 border-t border-secondary-foreground/10 pt-4">
                    <div className="flex justify-between font-body">
                      <span className="text-secondary-foreground/70">Condomínio</span>
                      <span className="font-semibold">R$ 2.500/mês</span>
                    </div>
                    <div className="flex justify-between font-body">
                      <span className="text-secondary-foreground/70">IPTU</span>
                      <span className="font-semibold">R$ 4.240/ano</span>
                    </div>
                  </div>
                </div>
                <div className="mt-8 space-y-3">
                  <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                    <Button variant="gold" className="w-full">
                      <Phone className="w-5 h-5" />
                      Falar no WhatsApp
                    </Button>
                  </a>
                  <ScheduleVisitDialog
                    propertyName="Cobertura Duplex no Parque das Rosas"
                    neighborhood="Barra da Tijuca"
                    variant="outline"
                    size="default"
                    className="w-full border-accent text-accent hover:bg-accent hover:text-accent-foreground"
                    label="Quero conhecer este imóvel"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Gallery */}
        <section className="py-16 bg-muted" id="fotos">
          <div className="container mx-auto px-4">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-10 text-center">Galeria de Fotos</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {images.map((img, i) => (
                <button key={i} onClick={() => openLightbox(i)} className={`overflow-hidden rounded-lg cursor-pointer group relative ${i === 0 ? "col-span-2 row-span-2" : ""}`}>
                  <img src={img.src} alt={img.alt} className="w-full h-full object-cover aspect-square group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/10 transition-colors" />
                </button>
              ))}
            </div>
          </div>

          {lightboxIndex !== null && (
            <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={closeLightbox}>
              <button onClick={(e) => { e.stopPropagation(); closeLightbox(); }} className="absolute top-4 right-4 text-white/80 hover:text-white"><X className="w-8 h-8" /></button>
              <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-4 text-white/80 hover:text-white"><ChevronLeft className="w-10 h-10" /></button>
              <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-4 text-white/80 hover:text-white"><ChevronRight className="w-10 h-10" /></button>
              <img src={images[lightboxIndex].src} alt={images[lightboxIndex].alt} className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
            </div>
          )}
        </section>

        {/* Description - sem Escada Caracol */}
        <section className="py-16 bg-background" id="descricao">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-8 text-center">Seu Refúgio de Luxo na Barra da Tijuca</h2>
            <div className="space-y-6 font-body text-muted-foreground leading-relaxed text-lg">
              <p>Se você busca <strong className="text-foreground">conforto, espaço e momentos inesquecíveis de lazer</strong> no coração do Parque das Rosas, sua busca termina aqui.</p>
              <p>Esta magnífica <strong className="text-foreground">cobertura duplex de 140m²</strong> foi concebida para quem não abre mão de qualidade de vida e praticidade. São <strong className="text-foreground">3 quartos espaçosos, sendo 2 suítes</strong>, ambientes amplos e impecavelmente distribuídos, com ar-condicionado em todos os cômodos para seu conforto absoluto durante o ano inteiro.</p>
              <div className="bg-accent/10 rounded-xl p-8 border border-accent/30">
                <h3 className="font-display text-xl font-bold text-foreground mb-4">✦ Primeiro Pavimento</h3>
                <p>Quarto aconchegante com acesso direto ao <strong className="text-foreground">varandão panorâmico</strong>, perfeito para relaxar com uma vista privilegiada. Sala ampla, cozinha completa, área de serviço e dependência de empregada.</p>
              </div>
              <div className="bg-accent/10 rounded-xl p-8 border border-accent/30">
                <h3 className="font-display text-xl font-bold text-foreground mb-4">✦ Segundo Pavimento — O Grande Destaque</h3>
                <p><strong className="text-foreground">Suíte master integrada à área externa exclusiva</strong> com piscina privativa, pia de apoio e churrasqueira — o cenário ideal para reunir amigos e família nos melhores momentos.</p>
              </div>
              <p>Tudo isso a apenas <strong className="text-foreground">100 metros do Shopping</strong>, oferecendo comodidade incomparável no seu dia a dia.</p>
            </div>
          </div>
        </section>

        {/* Highlights - sem Escada Caracol */}
        <section className="py-16 bg-card">
          <div className="container mx-auto px-4">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4 text-center">Por Que Esta Cobertura é Única</h2>
            <p className="font-body text-muted-foreground text-center max-w-2xl mx-auto mb-12">Diferenciais que fazem deste imóvel uma oportunidade rara no Parque das Rosas</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {highlights.map((h) => (
                <div key={h.title} className="group p-6 rounded-xl border border-border hover:border-accent/30 transition-colors bg-background shadow-sm">
                  <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center mb-4 group-hover:bg-accent transition-colors">
                    <h.icon className="w-6 h-6 text-accent group-hover:text-accent-foreground transition-colors" />
                  </div>
                  <h3 className="font-display text-lg font-bold text-foreground mb-2">{h.title}</h3>
                  <p className="font-body text-muted-foreground text-sm">{h.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Location - Mapa corrigido */}
        <section className="py-16 bg-background" id="localizacao">
          <div className="container mx-auto px-4">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4 text-center">Localização Privilegiada</h2>
            <p className="font-body text-muted-foreground text-center max-w-2xl mx-auto mb-10">Av. Marechal Henrique Lott, 70 — Parque das Rosas, Barra da Tijuca, Rio de Janeiro-RJ</p>
            <div className="rounded-xl overflow-hidden border border-border shadow-lg">
              <iframe
                title="Localização Av. Marechal Henrique Lott, 70 - Parque das Rosas"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3673.3!2d-43.3618!3d-22.9993!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x9bda2dd7a1d8f3%3A0x0!2sAv.+Marechal+Henrique+Lott%2C+70+-+Barra+da+Tijuca%2C+Rio+de+Janeiro!5e0!3m2!1spt-BR!2sbr!4v1"
                className="w-full h-[400px]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
              {[
                { label: "Shopping", dist: "100m" },
                { label: "Praia", dist: "10 min" },
                { label: "Metrô", dist: "15 min" },
                { label: "Escolas", dist: "Próximo" },
              ].map((item) => (
                <div key={item.label} className="text-center p-4 rounded-lg bg-muted">
                  <p className="font-display text-2xl font-bold text-accent">{item.dist}</p>
                  <p className="font-body text-sm text-muted-foreground">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Lead Form - Não Encontrou a Cobertura ideal? */}
        <section className="py-16 bg-accent/5 border-y border-accent/20">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-3">
                Não Encontrou a Cobertura Ideal?
              </h2>
              <p className="font-body text-muted-foreground mb-8">
                Conte-nos o que você procura e encontraremos as melhores opções de cobertura na Barra da Tijuca para você. Sem compromisso!
              </p>

              {leadSubmitted ? (
                <div className="py-12 bg-card rounded-xl border border-border shadow-sm">
                  <div className="text-5xl mb-4">✅</div>
                  <h3 className="font-display text-2xl font-bold text-foreground mb-2">Recebemos sua solicitação!</h3>
                  <p className="font-body text-muted-foreground mb-6">Entraremos em contato em breve com opções exclusivas.</p>
                  <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                    <Button variant="gold">Ou fale agora pelo WhatsApp</Button>
                  </a>
                </div>
              ) : (
                <form onSubmit={handleLeadSubmit} className="space-y-5 text-left bg-card rounded-xl border border-border p-8 shadow-sm">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="font-body text-sm text-muted-foreground mb-1.5 block">Nome completo*</label>
                      <Input
                        placeholder="Seu nome"
                        value={leadForm.name}
                        onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <label className="font-body text-sm text-muted-foreground mb-1.5 block">Telefone / WhatsApp*</label>
                      <Input
                        type="tel"
                        placeholder="(21) 99999-9999"
                        value={leadForm.phone}
                        onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="font-body text-sm text-muted-foreground mb-1.5 block">E-mail</label>
                    <Input
                      type="email"
                      placeholder="seu@email.com"
                      value={leadForm.email}
                      onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="font-body text-sm text-muted-foreground mb-1.5 block">O que você procura?</label>
                    <textarea
                      placeholder="Ex: Cobertura com 4 quartos na Barra, com vista para o mar..."
                      rows={3}
                      value={leadForm.message}
                      onChange={(e) => setLeadForm({ ...leadForm, message: e.target.value })}
                      className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:text-sm resize-none"
                    />
                  </div>
                  <Button type="submit" variant="gold" size="lg" className="w-full">
                    Quero Receber Opções de Cobertura
                  </Button>
                  <p className="font-body text-xs text-muted-foreground text-center">
                    Seus dados estão seguros. Não compartilhamos com terceiros.
                  </p>
                </form>
              )}
            </div>
          </div>
        </section>

        {/* Contact / Schedule */}
        <section className="py-16 bg-secondary" id="agendar">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-secondary-foreground mb-3 text-center">Agende Sua Visita Agora</h2>
              <p className="font-body text-secondary-foreground/70 text-center mb-10">Preencha seus dados e entraremos em contato em minutos. Sem compromisso!</p>

              {contactSubmitted ? (
                <div className="text-center py-12 bg-secondary-foreground/5 rounded-xl border border-secondary-foreground/10">
                  <div className="text-5xl mb-4">✅</div>
                  <h3 className="font-display text-2xl font-bold text-secondary-foreground mb-2">Mensagem Enviada!</h3>
                  <p className="font-body text-secondary-foreground/70 mb-6">Entraremos em contato em breve.</p>
                  <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                    <Button variant="gold">Ou fale agora pelo WhatsApp</Button>
                  </a>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} className="space-y-5">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="font-body text-sm text-secondary-foreground/70 mb-1.5 block">Nome completo*</label>
                      <Input
                        placeholder="Seu nome"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        required
                        className="bg-secondary-foreground/5 border-secondary-foreground/10 text-secondary-foreground placeholder:text-secondary-foreground/30"
                      />
                    </div>
                    <div>
                      <label className="font-body text-sm text-secondary-foreground/70 mb-1.5 block">Telefone / WhatsApp*</label>
                      <Input
                        type="tel"
                        placeholder="(21) 99999-9999"
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                        required
                        className="bg-secondary-foreground/5 border-secondary-foreground/10 text-secondary-foreground placeholder:text-secondary-foreground/30"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="font-body text-sm text-secondary-foreground/70 mb-1.5 block">E-mail</label>
                    <Input
                      type="email"
                      placeholder="seu@email.com"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      className="bg-secondary-foreground/5 border-secondary-foreground/10 text-secondary-foreground placeholder:text-secondary-foreground/30"
                    />
                  </div>
                  <div>
                    <label className="font-body text-sm text-secondary-foreground/70 mb-1.5 block">Mensagem</label>
                    <textarea
                      placeholder="Gostaria de agendar uma visita..."
                      rows={3}
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      className="flex w-full rounded-md border bg-secondary-foreground/5 border-secondary-foreground/10 text-secondary-foreground placeholder:text-secondary-foreground/30 px-3 py-2 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:text-sm resize-none"
                    />
                  </div>
                  <Button type="submit" variant="gold" size="lg" className="w-full text-lg">
                    Quero Agendar Minha Visita
                  </Button>
                  <p className="font-body text-xs text-secondary-foreground/40 text-center">
                    Seus dados estão seguros. Não compartilhamos com terceiros.
                  </p>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-secondary py-10 border-t border-border">
        <div className="container mx-auto px-4 text-center">
          <p className="font-display text-xl font-bold text-accent mb-1">TOPCLASS IMÓVEIS</p>
          <p className="font-body text-sm text-secondary-foreground/60 mb-2">CRECI 83590 · Paulo Miranda — Consultor Imobiliário</p>
          <p className="font-body text-sm text-secondary-foreground/50 mb-2">E-mail: prmirandaimoveis@gmail.com</p>
          <p className="font-body text-sm text-secondary-foreground/50 mb-4">
            Centro Empresarial Barra Shopping — Av. das Américas, 4200 - Bloco 1, Sala 305<br />
            Barra da Tijuca, Rio de Janeiro - RJ, 22640-907
          </p>
          <p className="font-body text-xs text-secondary-foreground/30 mt-6">
            © {new Date().getFullYear()} TOPCLASS Imóveis. Todos os direitos reservados.
          </p>
        </div>
      </footer>

      <WhatsAppFloatingButton message="Olá! Tenho interesse na Cobertura Duplex no Parque das Rosas - R$ 1.600.000. Gostaria de agendar uma visita!" />
    </div>
  );
};

export default CoberturaParqueDasRosas;
